import { useEffect, useState } from "react";
import { X, Loader2, Plus, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { travelApi } from "@/lib/travelApi";

export default function CreateExperienceModal({ open, onClose, onCreated, initialPlace = null }) {
  const [form, setForm] = useState({
    title: "",
    body: "",
    location_name: "",
    country: "",
    place_id: "",
    lat: "",
    lng: "",
    tips: "",
  });
  const [photos, setPhotos] = useState([""]); // array of image URLs
  const [submitting, setSubmitting] = useState(false);
  const [places, setPlaces] = useState([]);

  useEffect(() => {
    if (!open) return;
    travelApi.listPlaces({ limit: 60 }).then(setPlaces).catch(() => setPlaces([]));
  }, [open]);

  // Pre-fill from initialPlace (when invoked from PlaceDetailModal)
  useEffect(() => {
    if (!open || !initialPlace) return;
    setForm((f) => ({
      ...f,
      place_id: initialPlace.id || "",
      location_name: `${initialPlace.city ? initialPlace.city + ", " : ""}${initialPlace.country || initialPlace.name}`,
      country: initialPlace.country || "",
      lat: initialPlace.lat != null ? String(initialPlace.lat) : "",
      lng: initialPlace.lng != null ? String(initialPlace.lng) : "",
    }));
  }, [open, initialPlace]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPlaceSelect = (pid) => {
    if (!pid) { update("place_id", ""); return; }
    const p = places.find((x) => x.id === pid);
    if (!p) return;
    setForm((f) => ({
      ...f,
      place_id: pid,
      location_name: `${p.city ? p.city + ", " : ""}${p.country || p.name}`,
      country: p.country || "",
      lat: p.lat != null ? String(p.lat) : f.lat,
      lng: p.lng != null ? String(p.lng) : f.lng,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        location_name: form.location_name.trim(),
        country: form.country.trim(),
        place_id: form.place_id || null,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
        photos: photos.map((p) => p.trim()).filter(Boolean),
        tips: form.tips.trim(),
      };
      const created = await travelApi.createExperience(payload);
      toast.success("Experiencia publicada");
      onCreated?.(created);
      onClose();
      // reset
      setForm({ title: "", body: "", location_name: "", country: "", place_id: "", lat: "", lng: "", tips: "" });
      setPhotos([""]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo publicar la experiencia";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalización no soportada"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update("lat", pos.coords.latitude.toFixed(4));
        update("lng", pos.coords.longitude.toFixed(4));
        toast.success("Coordenadas añadidas");
      },
      () => toast.error("No se pudo obtener tu ubicación"),
      { timeout: 8000 }
    );
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="exp-create-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="bg-cream w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[88vh] rounded-t-3xl sm:rounded-3xl border hairline shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-5 border-b hairline shrink-0">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-coral">◊ Nueva experiencia</p>
            <h3 className="font-serif text-2xl tracking-tight">Compártela con la comunidad</h3>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border hairline grid place-items-center hover:bg-cream-card" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">Título *</label>
            <input
              data-testid="exp-create-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              maxLength={140}
              placeholder="p. ej. Una semana entre cerezos en Kioto"
              className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral"
              required
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">Contá tu experiencia</label>
            <textarea
              data-testid="exp-create-body"
              value={form.body}
              onChange={(e) => update("body", e.target.value)}
              maxLength={5000}
              rows={4}
              placeholder="Lo que viste, lo que sentiste, cómo te movés…"
              className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral resize-none"
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">
              Vincular a un destino <span className="text-ink-mid/70 normal-case font-sans">(opcional)</span>
            </label>
            <select
              data-testid="exp-create-place"
              value={form.place_id}
              onChange={(e) => onPlaceSelect(e.target.value)}
              className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral"
            >
              <option value="">— Sin vincular —</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.country}
                </option>
              ))}
            </select>
            {form.place_id && (
              <p className="text-[11px] text-coral mt-1 font-mono">Tu experiencia aparecerá en el detalle de este destino.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">Lugar</label>
              <input
                data-testid="exp-create-location"
                value={form.location_name}
                onChange={(e) => update("location_name", e.target.value)}
                placeholder="Tulum, México"
                className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">País</label>
              <input
                data-testid="exp-create-country"
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                placeholder="México"
                className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">Latitud</label>
              <input
                value={form.lat}
                onChange={(e) => update("lat", e.target.value)}
                placeholder="20.21"
                className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">Longitud</label>
              <input
                value={form.lng}
                onChange={(e) => update("lng", e.target.value)}
                placeholder="-87.46"
                className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={useMyLocation}
            data-testid="exp-create-geo"
            className="text-xs text-coral hover:underline inline-flex items-center gap-1"
          >
            <MapPin className="w-3 h-3" /> Usar mi ubicación actual
          </button>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">Tip del viajero</label>
            <textarea
              data-testid="exp-create-tips"
              value={form.tips}
              onChange={(e) => update("tips", e.target.value)}
              maxLength={1000}
              rows={2}
              placeholder="Un consejo concreto para el próximo viajero…"
              className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral resize-none"
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">Fotos (URL)</label>
            <div className="space-y-2">
              {photos.map((p, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    value={p}
                    onChange={(e) => setPhotos((arr) => arr.map((x, i) => (i === idx ? e.target.value : x)))}
                    placeholder="https://…"
                    className="flex-1 bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral"
                  />
                  {photos.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setPhotos((arr) => arr.filter((_, i) => i !== idx))}
                      className="w-9 h-9 rounded-full border hairline grid place-items-center hover:bg-cream-card"
                      aria-label="Quitar foto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {photos.length < 6 && (
                <button
                  type="button"
                  onClick={() => setPhotos((arr) => [...arr, ""])}
                  className="text-xs text-coral hover:underline inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Añadir otra foto
                </button>
              )}
            </div>
          </div>
        </div>

        <footer className="p-5 border-t hairline shrink-0 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 rounded-full border hairline hover:bg-cream-card">
            Cancelar
          </button>
          <button
            type="submit"
            data-testid="exp-create-submit"
            disabled={submitting || !form.title.trim()}
            className="text-xs px-5 py-2 rounded-full bg-ink text-cream hover:bg-coral transition disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Publicar experiencia
          </button>
        </footer>
      </form>
    </div>
  );
}
